import { MessageCircle, Mail, Phone, Instagram, Facebook, MapPin } from "lucide-react";
import Seo from "../components/Seo";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import { IMG, WHATSAPP_LINK, CONTACT_EMAIL, CONTACT_PHONE } from "../data/content";

const CHANNELS = [
  {
    icon: MessageCircle,
    title: "WhatsApp",
    value: "+91 98116 69100",
    note: "The fastest way to reach us — messages are answered personally.",
    href: WHATSAPP_LINK,
    testid: "contact-whatsapp",
  },
  {
    icon: Mail,
    title: "Email",
    value: CONTACT_EMAIL,
    note: "For detailed plans, dates and longer questions.",
    href: `mailto:${CONTACT_EMAIL}`,
    testid: "contact-email",
  },
  {
    icon: Phone,
    title: "Telephone",
    value: CONTACT_PHONE,
    note: "Remember — the forest end of the line is often out of signal.",
    href: `tel:${CONTACT_PHONE.replace(/\s/g, "")}`,
    testid: "contact-phone",
  },
];

export default function ContactPage() {
  return (
    <main data-testid="contact-page">
      <Seo
        title="Contact The Jamoon | Corbett Wilderness Lodge"
        description="Contact The Jamoon in Corbett to enquire about accommodation, packages, experiences and planning your wilderness stay."
        path="/contact"
        image={IMG.misty}
        imageAlt="Birdlife in the forest surrounding The Jamoon in Corbett"
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }]}
      />
      <PageHero
        testid="contact-hero"
        image={IMG.misty}
        imageAlt="Birdlife in the forest surrounding The Jamoon in Corbett"
        eyebrow="Contact"
        title={<>Contact <span className="italic text-brass">The Jamoon</span></>}
        sub="Every stay begins with a conversation. Ours are answered by people, not systems."
      />

      <section className="mx-auto max-w-7xl px-6 py-24 sm:px-12 sm:py-28 lg:px-20">
        <div className="grid gap-8 md:grid-cols-3">
          {CHANNELS.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.1}>
              <a
                data-testid={c.testid}
                href={c.href}
                target={c.href.startsWith("http") ? "_blank" : undefined}
                rel={c.href.startsWith("http") ? "noopener noreferrer" : undefined}
                className="group block border border-brass/20 p-8 transition-colors duration-500 hover:border-brass/60"
              >
                <c.icon className="h-6 w-6 text-brass" />
                <h2 className="mt-5 font-serif text-2xl font-light text-ivory transition-colors duration-300 group-hover:text-brass">
                  {c.title}
                </h2>
                <p className="mt-2 text-sm font-medium text-ivory/85">{c.value}</p>
                <p className="mt-3 text-sm leading-relaxed text-ivory/55">{c.note}</p>
              </a>
            </Reveal>
          ))}
        </div>

        <div className="mt-16 grid gap-8 md:grid-cols-2">
          <Reveal>
            <div className="border border-ivory/10 p-8" data-testid="contact-location">
              <MapPin className="h-6 w-6 text-brass" />
              <h2 className="mt-5 font-serif text-2xl font-light text-ivory">Where we are</h2>
              <p className="mt-3 max-w-md text-sm leading-relaxed text-ivory/60">
                Village Jamoon, in the forest beside the Lohachaur area of Corbett Tiger
                Reserve, Uttarakhand, India. The vehicle journey ends at village Bhasedi — the
                last 3.5 km are walked through the forest.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="border border-ivory/10 p-8" data-testid="contact-social">
              <h2 className="font-serif text-2xl font-light text-ivory">Follow the forest</h2>
              <p className="mt-3 text-sm leading-relaxed text-ivory/60">
                Camera-trap frames, birding mornings and life at the lodge — as it happens.
              </p>
              <div className="mt-5 flex gap-4">
                <a data-testid="contact-instagram" href="https://www.instagram.com/jamooncorbett/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 border border-ivory/20 px-4 py-2.5 text-xs tracking-[0.2em] text-ivory/75 transition-colors duration-300 hover:border-brass hover:text-brass">
                  <Instagram className="h-4 w-4" /> INSTAGRAM
                </a>
                <a data-testid="contact-facebook" href="https://www.facebook.com/Jamoon-Corbett-114914090639354" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 border border-ivory/20 px-4 py-2.5 text-xs tracking-[0.2em] text-ivory/75 transition-colors duration-300 hover:border-brass hover:text-brass">
                  <Facebook className="h-4 w-4" /> FACEBOOK
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
